import React, { useRef } from 'react';
import { Grid, Dialog } from '@mui/material';
import { createRoot } from 'react-dom/client';
import { useSearchParams } from 'react-router-dom';
import { Connector } from '@domain/connection/models/Connector';
import { container } from '@infrastructure/container';
import { ClientType } from '@domain/connection/models/Connector';
import BolCredentialsForm from '../../bol/components/BolCredentialsForm';
import MontaCredentialsForm from '../../monta/components/MontaCredentialsForm';
import { useQuery, QueryClient, QueryClientProvider, useQueryClient } from '@tanstack/react-query';
import ProductFlowCredentialsForm from '../../product_flow/components/ProductFlowCredentialsForm';
import { Connection } from '@domain/connection/models/Connection';
import MarketplaceConnectorButton from './MarketplaceConnectorButton';
import WooCommerceCredentialsForm from '@features/connections/woocommerce/components/WooCommerceCredentialsForm';
import ShopifyConnectForm from '@features/connections/shopify/components/ShopifyConnectForm';
import AmazonCredentialsForm from '@features/connections/amazon/components/AmazonCredentialsForm';
import ChannelDockCredentialsForm from '@features/connections/channeldock/components/ChannelDockCredentialsForm';

interface MarketplaceGridProps {
  onNewConnection: (connection: Connection) => void;
}

export const MarketplaceGrid: React.FC<MarketplaceGridProps> = ({ onNewConnection }) => {


  const queryClient = useQueryClient();

  const [searchParams] = useSearchParams();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const rootRef = useRef<any>(null);

  const { data: connectors } = useQuery({
    queryKey: ['marketplace-connectors'],
    queryFn: async () => {
      const response = await container.marketplaceRepository.fetchConnectors('marketplace');
      if (response.status === 'success') {
        return response.data;
      }
      return [];
    },
  })

  const cleanup = () => {
    if (containerRef.current) {
      containerRef.current.remove();
      containerRef.current = null;
    }
    if (rootRef.current) {
      rootRef.current.unmount();
      rootRef.current = null;
    }
  };

  const handleNewConnection = (connection: Connection) => {
    queryClient.invalidateQueries({ queryKey: ['connections'] });
    cleanup();
    onNewConnection(connection);
  };

  const handleConnectorClose = () => {
    cleanup();
  };

  const handleConnectorClick = (connector: Connector) => {
    // Always cleanup first to ensure clean state
    cleanup();

    // Create new container and root for this dialog instance
    containerRef.current = document.createElement('div');
    document.body.appendChild(containerRef.current);
    rootRef.current = createRoot(containerRef.current);

    let component;

    if (connector.type === ClientType.BOL) {
      component = <BolCredentialsForm onNewConnection={onNewConnection} onClose={handleConnectorClose} searchParams={searchParams.toString()} />;
    }

    if (connector.type === ClientType.MONTA) {
      component = <MontaCredentialsForm onNewConnection={onNewConnection} onClose={handleConnectorClose} />;
    }

    if (connector.type === ClientType.PRODUCT_FLOW) {
      component = <ProductFlowCredentialsForm onNewConnection={handleNewConnection} onClose={handleConnectorClose} />;
    }

    if (connector.type === ClientType.WOOCOMMERCE) {  
      component = <WooCommerceCredentialsForm onNewConnection={handleNewConnection} onClose={handleConnectorClose} />;
    }

    if (connector.type === ClientType.SHOPIFY) {
      component = <ShopifyConnectForm onClose={handleConnectorClose} />;
    }

    if (connector.type === ClientType.AMAZON) {
      component = <AmazonCredentialsForm onNewConnection={handleNewConnection} onClose={handleConnectorClose} />;
    }

    if (connector.type === ClientType.CHANNELDOCK) {
      component = <ChannelDockCredentialsForm onNewConnection={handleNewConnection} onClose={handleConnectorClose} />;
    }

    // Render dialog with open={true} since we're creating a fresh instance
    rootRef.current.render(
      <Dialog open={true} onClose={handleConnectorClose} maxWidth="sm" fullWidth>
        {component}
      </Dialog>
    );
  };

  return (
    <Grid container spacing={2}>
      {connectors?.map((connector) => (
        <Grid item xs={4} key={connector.type}>
          <MarketplaceConnectorButton
            connector={connector}
            onClick={() => handleConnectorClick(connector)}
          />
        </Grid>
      ))}
    </Grid>
  );
}; 